// src/orders/OrderEdit.tsx

import React, { FC, useState } from 'react';
import {
    Edit,
    useNotify,
    useRedirect,
    TopToolbar,
    ListButton,
    usePermissions,
    useDelete,
    useRecordContext,
} from 'react-admin';
import {
    Button as MUIButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import useAuthorize from '../hooks/useAuthorize';
import { UserRole } from '../types/types';
import OrderForm, { OrderFormToolbar, transformOrder } from './OrderForm';

// Normalize permission values into ['Admin','ProductAdmin',…']
const normalizeRoles = (perms: any): string[] => {
    const arr = Array.isArray(perms) ? perms : perms != null ? [perms] : [];
    return arr.map(p =>
        typeof p === 'number'
            ? (UserRole[p] as unknown as string)
            : String(p)
    );
};

// Replace both the top-right Delete and the form toolbar delete
const CustomDeleteButton: FC = () => {
    const record = useRecordContext<{ id: number }>();
    const { permissions } = usePermissions<any>();
    console.log('🔍 [CustomDeleteButton] raw permissions:', permissions);

    const roles = normalizeRoles(permissions);
    console.log('🔍 [CustomDeleteButton] normalized roles:', roles);

    const isAdmin = roles.includes('Admin');
    const isProductAdmin = roles.includes('ProductAdmin');
    console.log(
        '🔍 [CustomDeleteButton] isAdmin:', isAdmin,
        'isProductAdmin:', isProductAdmin
    );

    // hooks for actual delete
    const [deleteOne, { isLoading }] = useDelete();
    const notify = useNotify();
    const redirect = useRedirect();

    const [openConfirm, setOpenConfirm] = useState(false);
    const [openDenied, setOpenDenied] = useState(false);

    const handleClick = () => {
        if (isAdmin) {
            console.log('🔍 [CustomDeleteButton] Admin clicked → confirm dialog');
            setOpenConfirm(true);
        } else if (isProductAdmin) {
            console.log('🔍 [CustomDeleteButton] ProductAdmin clicked → denied dialog');
            setOpenDenied(true);
        }
    };

    const confirmDelete = async () => {
        console.log('🔍 [CustomDeleteButton] Admin confirmed → calling deleteOne');
        await deleteOne('orders', { id: record.id });
        notify('Order deleted', { type: 'info' });
        setOpenConfirm(false);
        redirect('/orders');
    };

    return (
        <>
            <MUIButton
                variant="text"
                startIcon={<DeleteIcon />}
                onClick={handleClick}
                disabled={isLoading}
            >
                Delete
            </MUIButton>

            {/* Admin: actual confirmation */}
            <Dialog
                open={openConfirm}
                onClose={() => setOpenConfirm(false)}
            >
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to delete this order?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MUIButton onClick={() => setOpenConfirm(false)}>
                        Cancel
                    </MUIButton>
                    <MUIButton onClick={confirmDelete} color="error">
                        Delete
                    </MUIButton>
                </DialogActions>
            </Dialog>

            {/* ProductAdmin: denied */}
            <Dialog
                open={openDenied}
                onClose={() => setOpenDenied(false)}
            >
                <DialogTitle>Permission Denied</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        You don’t have permission to delete orders. Please contact your administrators.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <MUIButton onClick={() => setOpenDenied(false)}>
                        OK
                    </MUIButton>
                </DialogActions>
            </Dialog>
        </>
    );
};

// Top toolbar: only List + our CustomDelete
const OrderEditActions: FC<any> = props => {
    useAuthorize([UserRole.Admin, UserRole.ProductAdmin]);
    return (
        <TopToolbar>
            <ListButton />
            <CustomDeleteButton {...props} />
        </TopToolbar>
    );
};

export const OrderEdit: FC<any> = props => {
    const notify = useNotify();
    const redirect = useRedirect();

    const onSuccess = () => {
        notify('Order updated successfully', { type: 'info' });
        redirect('/orders');
    };

    return (
        <Edit
            {...props}
            actions={<OrderEditActions />}
            mutationMode="pessimistic"
            mutationOptions={{ onSuccess }}
            transform={transformOrder}
        >
            <OrderForm toolbar={<OrderFormToolbar />} />
        </Edit>
    );
};

export default OrderEdit;
